import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import useProjectStore from '../stores/projectStore';
import { useProjectIntegrations } from '../hooks/useProjectIntegrations';
import { useAnalyses } from '../hooks/useAnalysis';
import { useSeoKeywords, useSeoMeasures } from '../hooks/useSeoKeywords';
import { useProjectReports } from '../hooks/useProjectReports';
import ProjectContextBadge from '../components/layout/ProjectContextBadge';
import Modal from '../components/ui/Modal';
import api from '../lib/axios';

// ── KPI Card ────────────────────────────────────────────────────────────
function KpiCard({ label, value, sub, accent, href }) {
  const inner = (
    <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-4 h-full">
      <div className="text-[10.5px] font-semibold tracking-wider text-gray-400 uppercase">{label}</div>
      <div className="text-[26px] font-bold mt-1.5" style={{ color: accent || '#0F766E' }}>
        {value ?? '—'}
      </div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
  return href ? <Link to={href} className="block hover:-translate-y-0.5 transition-transform">{inner}</Link> : inner;
}

// ── Inline Sparkline SVG ────────────────────────────────────────────────
function Sparkline({ points, color = '#0F766E', invert = false, height = 40, width = 160 }) {
  if (!points || points.length < 2) {
    return <div className="text-[11px] text-gray-300">Nicht genug Daten</div>;
  }
  const min = Math.min(...points);
  const max = Math.max(...points);
  const range = max - min || 1;
  const step = width / (points.length - 1);
  const d = points
    .map((p, i) => {
      const x = i * step;
      const yRaw = (p - min) / range;
      const y = invert ? yRaw * height : height - yRaw * height;
      return `${i === 0 ? 'M' : 'L'} ${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
  return (
    <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-10">
      <path d={d} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ── Modul-Section Wrapper ───────────────────────────────────────────────
function Section({ title, icon, href, children }) {
  return (
    <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-[14px] font-semibold text-gray-900 flex items-center gap-2">
          <span className="text-teal text-[15px]">{icon}</span>
          {title}
        </h3>
        {href && (
          <Link to={href} className="text-[11.5px] text-teal font-medium hover:underline">
            Alle →
          </Link>
        )}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ text }) {
  return <div className="text-[12px] text-gray-400 py-4 text-center">{text}</div>;
}

// ── Share-Link-Modal ────────────────────────────────────────────────────
function ShareLinkModal({ projectId, cockpit, onClose }) {
  const qc = useQueryClient();
  const config = cockpit?.config || {};
  const token = config.share_token;
  const [isShared, setIsShared] = useState(!!config.is_shared);
  const [hasPassword, setHasPassword] = useState(!!config.has_password);
  const [password, setPassword] = useState('');
  const [saving, setSaving] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const baseUrl = `${window.location.origin}/share/cockpit/${token || ''}`;

  const patch = async (payload) => {
    setSaving(true);
    setError(null);
    try {
      const { data } = await api.patch(
        `/projects/${projectId}/integrations/cockpit/share`,
        payload,
        { skipProject: true }
      );
      qc.invalidateQueries({ queryKey: ['project-integrations', projectId] });
      return data;
    } catch (e) {
      setError(e.response?.data?.message || e.message);
      return null;
    } finally {
      setSaving(false);
    }
  };

  const toggleShare = async (on) => {
    const res = await patch({ is_shared: on });
    if (res) setIsShared(res.is_shared);
  };

  const setNewPassword = async () => {
    if (!password) return;
    const res = await patch({ password });
    if (res) {
      setHasPassword(res.has_password);
      setPassword('');
    }
  };

  const removePassword = async () => {
    const res = await patch({ remove_password: true });
    if (res) setHasPassword(res.has_password);
  };

  const rotate = async () => {
    if (!confirm('Neuen Token generieren? Der alte Link wird ungültig.')) return;
    await patch({ rotate_token: true });
  };

  const copy = () => {
    navigator.clipboard.writeText(baseUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Modal onClose={onClose}>
      <div className="w-[480px]">
        <h2 className="text-[17px] font-bold mb-1">Cockpit teilen</h2>
        <p className="text-[12.5px] text-gray-500 mb-5">
          Öffentlicher Link für den Kunden — read-only, optional passwortgeschützt.
        </p>

        <label className="flex items-center gap-3 mb-4 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={isShared}
            disabled={saving}
            onChange={(e) => toggleShare(e.target.checked)}
            className="accent-teal w-4 h-4"
          />
          <div>
            <div className="text-[13px] font-medium text-gray-900">Share-Link aktivieren</div>
            <div className="text-[11px] text-gray-500">Beim ersten Aktivieren wird ein Token generiert.</div>
          </div>
        </label>

        {isShared && token && (
          <>
            <div className="mb-4">
              <div className="text-[11px] text-gray-500 mb-1">Share-URL</div>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={baseUrl}
                  onClick={(e) => e.target.select()}
                  className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-[12px] font-mono bg-gray-50"
                />
                <button
                  onClick={copy}
                  className="px-3 py-2 bg-teal text-white rounded-lg text-[12px] font-medium hover:opacity-90"
                >
                  {copied ? '✓' : 'Kopieren'}
                </button>
              </div>
            </div>

            <div className="bg-gray-50 rounded-lg p-3 mb-4">
              <div className="text-[12px] font-semibold text-gray-700 mb-2">Passwortschutz</div>
              {hasPassword ? (
                <div className="flex items-center justify-between gap-3">
                  <div className="text-[11.5px] text-gray-600 flex items-center gap-1.5">
                    <span className="text-teal">✓</span> Passwort ist gesetzt
                  </div>
                  <button
                    onClick={removePassword}
                    disabled={saving}
                    className="text-[11.5px] text-red-600 hover:underline disabled:opacity-50"
                  >
                    Entfernen
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input
                    type="password"
                    placeholder="Passwort vergeben"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="flex-1 border border-gray-200 rounded-lg px-3 py-1.5 text-[12px] outline-none focus:border-teal bg-white"
                  />
                  <button
                    onClick={setNewPassword}
                    disabled={saving || !password || password.length < 3}
                    className="px-3 py-1.5 bg-sidebar text-white rounded-lg text-[12px] font-medium hover:opacity-90 disabled:opacity-50"
                  >
                    Setzen
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-between items-center">
              <button
                onClick={rotate}
                disabled={saving}
                className="text-[11.5px] text-gray-500 hover:text-gray-700 disabled:opacity-50"
              >
                ↻ Token rotieren (alten Link invalidieren)
              </button>
              <a
                href={baseUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[11.5px] text-teal font-medium hover:underline"
              >
                Vorschau öffnen ↗
              </a>
            </div>
          </>
        )}

        {error && (
          <div className="text-[11.5px] text-red-700 bg-red-50 rounded-md px-3 py-2 mt-4">{error}</div>
        )}

        <div className="flex justify-end mt-5">
          <button onClick={onClose} className="px-4 py-2 text-[13px] text-gray-600 hover:bg-gray-50 rounded-lg">
            Schließen
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ── Page ────────────────────────────────────────────────────────────────
export default function CockpitDashboard() {
  const currentProjectId = useProjectStore((s) => s.currentProjectId);
  const currentProject = useProjectStore((s) =>
    s.projects.find((p) => p.id === s.currentProjectId)
  );
  const [shareOpen, setShareOpen] = useState(false);

  const { data: integrationsData } = useProjectIntegrations(currentProjectId);
  const { data: analysesData, isLoading: analysesLoading } = useAnalyses();
  const { data: keywordsData, isLoading: keywordsLoading } = useSeoKeywords();
  const { data: measuresData, isLoading: measuresLoading } = useSeoMeasures();
  const { data: reportsData, isLoading: reportsLoading } = useProjectReports();

  const cockpit = integrationsData?.integrations?.find((i) => i.type === 'cockpit');
  const title = cockpit?.config?.label?.trim() || 'Kunden Cockpit';

  // Gebuchte Module ableiten, damit das Cockpit zeigt, was wirklich aktiv ist
  const bookedTypes = new Set(
    (integrationsData?.integrations || [])
      .filter((i) => i.is_booked && i.type !== 'cockpit')
      .map((i) => i.type)
  );

  const analyses = analysesData?.data || [];
  const keywords = keywordsData?.keywords || [];
  const measures = measuresData?.measures || [];
  const reports = reportsData || [];

  // ── KPI-Berechnungen ──
  const kpis = useMemo(() => {
    const doneAnalyses = analyses.filter((a) => a.status === 'done' && typeof a.score === 'number');
    const avgScore = doneAnalyses.length
      ? Math.round(doneAnalyses.reduce((s, a) => s + a.score, 0) / doneAnalyses.length)
      : null;

    const rankedKw = keywords
      .filter((k) => k.position_current != null && k.position_current > 0)
      .sort((a, b) => Number(a.position_current) - Number(b.position_current));
    const topKw = rankedKw[0];

    const openMeasures = measures.filter((m) => m.status === 'open' || m.status === 'in_progress' || m.status === 'pending');

    return { avgScore, analysesCount: doneAnalyses.length, topKw, openMeasures: openMeasures.length, reportsCount: reports.length };
  }, [analyses, keywords, measures, reports]);

  // ── Sparkline-Daten: Rankings-Trend & Top-Analyse-Score-Verlauf ──
  const rankingsTrend = useMemo(() => {
    // Durchschnittliche Position pro Tag aus den jüngsten History-Einträgen aller Keywords
    const byDate = {};
    keywords.forEach((k) => {
      (k.history || []).forEach((h) => {
        if (!byDate[h.date]) byDate[h.date] = { sum: 0, n: 0 };
        byDate[h.date].sum += Number(h.position) || 0;
        byDate[h.date].n += 1;
      });
    });
    const sortedDates = Object.keys(byDate).sort();
    return sortedDates.slice(-14).map((d) => byDate[d].sum / byDate[d].n);
  }, [keywords]);

  const scoreTrend = useMemo(() => {
    return analyses
      .filter((a) => a.status === 'done' && typeof a.score === 'number')
      .slice(-10)
      .map((a) => a.score);
  }, [analyses]);

  // ── No cockpit enabled ──
  if (!cockpit?.is_booked) {
    return (
      <div>
        <ProjectContextBadge />
        <div className="flex flex-col items-center justify-center text-center py-24 px-6">
          <div
            className="w-16 h-16 rounded-2xl flex items-center justify-center text-[28px] mb-5"
            style={{ backgroundColor: '#E6F4F2', color: '#0F766E' }}
          >
            ▦
          </div>
          <h1 className="text-[22px] font-bold text-gray-900 mb-2">Kunden Cockpit</h1>
          <p className="text-[13.5px] text-gray-500 max-w-md leading-relaxed">
            Für dieses Projekt ist das Cockpit noch nicht aktiviert. Gehe zur Projekt-Übersicht
            und aktiviere es über die Integrations-Karte „Kunden Cockpit".
          </p>
          <Link
            to="/"
            className="mt-5 inline-flex items-center bg-teal text-white rounded-lg px-4 py-2 text-[13px] font-medium hover:opacity-90"
          >
            → Zur Projekt-Übersicht
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-start justify-between mb-5">
        <div>
          <ProjectContextBadge />
          <h1 className="text-[23px] font-bold text-gray-900">{title}</h1>
          <p className="text-[13px] text-gray-500 mt-0.5">
            360° Performance für {currentProject?.name || 'dieses Projekt'} — aggregiert aus allen gebuchten Modulen
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShareOpen(true)}
            className="px-3.5 py-2 rounded-lg text-[12.5px] font-medium bg-teal text-white hover:opacity-90 flex items-center gap-1.5"
          >
            {cockpit?.config?.is_shared ? '⬡ Share-Link verwalten' : '⬡ Cockpit teilen'}
          </button>
        </div>
      </div>

      {shareOpen && (
        <ShareLinkModal
          projectId={currentProjectId}
          cockpit={cockpit}
          onClose={() => setShareOpen(false)}
        />
      )}

      {/* Hero KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        <KpiCard
          label="Ø Analyse-Score"
          value={kpis.avgScore != null ? `${kpis.avgScore}` : '—'}
          sub={kpis.analysesCount ? `${kpis.analysesCount} Analysen` : 'Noch keine Analysen'}
          accent="#0C9488"
          href="/cro/analyses"
        />
        <KpiCard
          label="Top-Position"
          value={kpis.topKw ? `#${Math.round(kpis.topKw.position_current)}` : '—'}
          sub={kpis.topKw ? kpis.topKw.keyword : 'Noch keine Rankings'}
          accent="#1C2E3C"
          href="/seo/rankings"
        />
        <KpiCard
          label="Offene Maßnahmen"
          value={kpis.openMeasures}
          sub={measuresLoading ? 'Lade…' : 'SEO-Backlog'}
          accent="#F59E0B"
          href="/seo/measures"
        />
        <KpiCard
          label="Monatsreports"
          value={kpis.reportsCount}
          sub={reportsLoading ? 'Lade…' : 'Generiert'}
          accent="#6D28D9"
          href="/project-reports"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-5">
        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[12.5px] font-semibold text-gray-700">Rankings-Trend</div>
            <div className="text-[10.5px] text-gray-400">Ø Position, letzte 14 Messungen</div>
          </div>
          {/* niedrigere Position = besseres Ranking, daher invert */}
          <Sparkline points={rankingsTrend} color="#1C2E3C" invert />
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-black/[.07] p-5">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[12.5px] font-semibold text-gray-700">CRO-Score-Verlauf</div>
            <div className="text-[10.5px] text-gray-400">letzte 10 Analysen</div>
          </div>
          <Sparkline points={scoreTrend} color="#0C9488" />
        </div>
      </div>

      {/* Modul-Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        <Section title="Letzte CRO-Analysen" icon="⟳" href="/cro/analyses">
          {analysesLoading ? (
            <EmptyState text="Lade…" />
          ) : analyses.length === 0 ? (
            <EmptyState text="Noch keine Analysen." />
          ) : (
            <div className="divide-y divide-gray-100">
              {analyses.slice(0, 5).map((a) => (
                <div key={a.id} className="py-2.5 flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="text-[12.5px] font-medium text-gray-900 truncate">{a.url}</div>
                    <div className="text-[10.5px] text-gray-400">
                      {a.page_type} · {new Date(a.created_at).toLocaleDateString('de')}
                    </div>
                  </div>
                  <div className="text-[14px] font-bold text-teal">{a.score ?? '—'}</div>
                </div>
              ))}
            </div>
          )}
        </Section>

        <Section title="Top-Rankings" icon="◉" href="/seo/rankings">
          {keywordsLoading ? (
            <EmptyState text="Lade…" />
          ) : keywords.length === 0 ? (
            <EmptyState text="Keine Keywords." />
          ) : (
            <div className="divide-y divide-gray-100">
              {keywords
                .filter((k) => k.position_current != null)
                .sort((a, b) => Number(a.position_current) - Number(b.position_current))
                .slice(0, 5)
                .map((k) => (
                  <div key={k.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-medium text-gray-900 truncate">{k.keyword}</div>
                      <div className="text-[10.5px] text-gray-400">
                        {k.impressions != null ? `${k.impressions} Impressionen` : k.category}
                      </div>
                    </div>
                    <div className="text-[13.5px] font-bold text-sidebar">
                      #{Math.round(Number(k.position_current))}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </Section>

        <Section title="Offene Maßnahmen" icon="☑" href="/seo/measures">
          {measuresLoading ? (
            <EmptyState text="Lade…" />
          ) : measures.length === 0 ? (
            <EmptyState text="Keine Maßnahmen im Backlog." />
          ) : (
            <div className="divide-y divide-gray-100">
              {measures
                .filter((m) => m.status !== 'done' && m.status !== 'completed')
                .slice(0, 5)
                .map((m) => (
                  <div key={m.id} className="py-2.5">
                    <div className="text-[12.5px] font-medium text-gray-900">{m.title || m.name}</div>
                    <div className="text-[10.5px] text-gray-400 mt-0.5">
                      {m.category} · {m.status}
                    </div>
                  </div>
                ))}
            </div>
          )}
        </Section>

        <Section title="Monatsreports" icon="▦" href="/project-reports">
          {reportsLoading ? (
            <EmptyState text="Lade…" />
          ) : reports.length === 0 ? (
            <EmptyState text="Keine Reports generiert." />
          ) : (
            <div className="divide-y divide-gray-100">
              {reports.slice(0, 5).map((r) => {
                const periodLabel = r.period_start
                  ? new Date(r.period_start).toLocaleDateString('de', { month: 'short', year: 'numeric' })
                  : null;
                return (
                  <div key={r.id} className="py-2.5 flex items-center justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-[12.5px] font-medium text-gray-900 truncate">
                        {r.title || periodLabel || `Report #${r.id}`}
                      </div>
                      <div className="text-[10.5px] text-gray-400">
                        {r.created_at ? new Date(r.created_at).toLocaleDateString('de') : '—'}
                      </div>
                    </div>
                    <span className="text-[10.5px] font-semibold px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">
                      {r.status || 'draft'}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </Section>
      </div>

      {/* Info-Footer: welche Module noch nicht aktiv sind */}
      {bookedTypes.size < 4 && (
        <div className="mt-5 bg-teal-light border border-teal/20 rounded-xl px-4 py-3 text-[12px] text-teal leading-snug">
          Tipp: Je mehr Module gebucht sind, desto vollständiger das Cockpit. Aktuell aktiv:{' '}
          <strong>{Array.from(bookedTypes).join(', ') || 'keine'}</strong>. GA4-Traffic und SEA-Daten
          fließen ein, sobald die entsprechenden Integrationen verbunden sind.
        </div>
      )}
    </div>
  );
}
